import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createApiVersion,
  deleteApiVersion,
  fetchVersioningStats,
  listApiVersions,
  setApiVersionStatus,
  setDefaultApiVersion,
  versionStatusMeta,
  versionedPath,
  VERSIONING_POLICY,
  type CoreApiVersion,
} from '@/lib/coreApiVersioning';
import { API_ENDPOINTS } from '@/lib/coreApi';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ApiVersionsTab({ org }: Props) {
  const [versions, setVersions] = useState<CoreApiVersion[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0, deprecated: 0, current: 'v1' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [form, setForm] = useState({ version: 'v2', label: 'API Core v2', notes: '' });
  const [creating, setCreating] = useState(false);
  const [previewPath, setPreviewPath] = useState('customers');
  const [previewVersion, setPreviewVersion] = useState('v1');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [v, s] = await Promise.all([listApiVersions(), fetchVersioningStats()]);
      setVersions(v);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const v = form.version.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!v) { setError('La version est requise (ex : v2).'); return; }
    setCreating(true);
    setError('');
    try {
      await createApiVersion({ version: v, label: form.label || `API ${v}`, releaseNotes: form.notes || null });
      setForm({ version: '', label: '', notes: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreating(false);
    }
  };

  const changeStatus = async (v: CoreApiVersion, status: 'active' | 'deprecated' | 'retired') => {
    setBusy(v.id);
    try { await setApiVersionStatus(v.id, status); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const makeDefault = async (v: CoreApiVersion) => {
    setBusy(v.id);
    try { await setDefaultApiVersion(v.id); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const remove = async (v: CoreApiVersion) => {
    setBusy(v.id);
    try { await deleteApiVersion(v.id); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">API versioning</span> : les APIs sont versionnées
        (<code className="bg-background-100 px-1 rounded">/api/v1/…</code> puis plus tard <code className="bg-background-100 px-1 rounded">/api/v2/…</code>).
        Une évolution ne casse jamais les intégrations existantes.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-git-branch-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Versions</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.active}</p>
          <p className="text-xs text-foreground-500">Actives</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-git-commit-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.current}</p>
          <p className="text-xs text-foreground-500">Version par défaut</p>
        </div>
      </div>

      {/* Versions */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-git-branch-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Versions de l’API</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : versions.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune version définie.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {versions.map((v) => {
              const sm = versionStatusMeta(v.status);
              return (
                <div key={v.id} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0 font-bold text-xs">{v.version}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate flex items-center gap-2">
                        {v.label || v.version}
                        {v.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900">par défaut</span>}
                      </p>
                      <p className="text-[11px] text-foreground-400 truncate">
                        <code className="bg-background-100 px-1 rounded">{versionedPath(v.version, '')}</code>
                        {v.release_notes ? ` · ${v.release_notes}` : ''}
                      </p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                    {busy === v.id && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                  </div>
                  <div className="mt-2 pl-12 flex flex-wrap gap-1.5">
                    {!v.is_default && v.status === 'active' && (
                      <button onClick={() => makeDefault(v)} disabled={busy === v.id} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer">Définir par défaut</button>
                    )}
                    {v.status !== 'deprecated' && (
                      <button onClick={() => changeStatus(v, 'deprecated')} disabled={busy === v.id} className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-medium whitespace-nowrap cursor-pointer">Déprécier</button>
                    )}
                    {v.status !== 'active' && (
                      <button onClick={() => changeStatus(v, 'active')} disabled={busy === v.id} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer">Réactiver</button>
                    )}
                    {!v.is_default && (
                      <button onClick={() => remove(v)} disabled={busy === v.id} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer">Supprimer</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Créer une version */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Publier une nouvelle version</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className={INPUT} placeholder="Version (ex : v2)" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
            <input className={INPUT} placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          <input className={INPUT} placeholder="Notes de version" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={300} />
          <button onClick={create} disabled={creating || !form.version.trim()} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {creating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>} Publier
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Politique */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-shield-check-line text-primary-600"></i>Politique de compatibilité</h4>
          <ul className="space-y-1.5">
            {VERSIONING_POLICY.map((p, i) => (
              <li key={i} className="text-[11px] text-foreground-600 flex items-start gap-1.5"><i className="ri-checkbox-blank-circle-line text-[7px] mt-1.5"></i>{p}</li>
            ))}
          </ul>
        </div>

        {/* Aperçu de chemin */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-link text-accent-600"></i>Aperçu du chemin versionné</h4>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-foreground-500 whitespace-nowrap">Version</span>
            <select className={INPUT} value={previewVersion} onChange={(e) => setPreviewVersion(e.target.value)}>
              {versions.map((v) => <option key={v.id} value={v.version}>{v.version}</option>)}
            </select>
          </div>
          <input className={INPUT} placeholder="Ressource (ex : customers)" value={previewPath} onChange={(e) => setPreviewPath(e.target.value)} />
          <p className="mt-2 text-xs text-foreground-700 bg-background-100 rounded-md p-2 break-all">
            <code>{versionedPath(previewVersion, previewPath)}</code>
          </p>
          <p className="mt-2 text-[11px] text-foreground-400">Endpoints documentés : {API_ENDPOINTS.length}.</p>
        </div>
      </div>
    </div>
  );
}